import { Component } from '@theme/component';

class ProductCardQuickAdd extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initElements();
    this.initData();
    
    this.setupEventListeners();
    this.setupHoverListeners();
    
    this.initOptionSelection();
  }
  
  initElements() {
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.optionsPanel = this.querySelector('[data-quick-add-options]');
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.quickAddButton = this.querySelector('[data-show-quick-add-options]');
    this.optionGroups = this.querySelectorAll('[data-quick-add-option]');
    this.variantDataElement = this.querySelector('[data-variant-data]');
  }
  
  initData() {
    if (this.variantDataElement) {
      const variantsScript = this.variantDataElement.querySelector('[data-product-variants]');
      const optionsScript = this.variantDataElement.querySelector('[data-product-options]');
      
      if (variantsScript) {
        this.variants = JSON.parse(variantsScript.textContent);
      }
      
      if (optionsScript) {
        this.productOptions = JSON.parse(optionsScript.textContent);
      }
      
      const metaElement = this.variantDataElement.querySelector('div[data-pre-selected-option-index]');
      if (metaElement) {
        this.preSelectedOptionIndex = parseInt(metaElement.dataset.preSelectedOptionIndex, 10);
        this.preSelectedOptionPosition = parseInt(metaElement.dataset.preSelectedOptionPosition, 10);
        this.totalOptions = parseInt(metaElement.dataset.totalOptions, 10);
      }
    }

    this.selectedOptions = new Array(this.totalOptions).fill(null);
    this.userSelectedOptions = new Set();
  }
  
  setupEventListeners() {
    this.optionGroups.forEach(group => {
      const inputs = group.querySelectorAll('input[type="radio"]');
      inputs.forEach(input => {
        input.addEventListener('change', this.handleOptionChange.bind(this), true);
      });
    });
    
    if (this.quickAddButton) {
      this.quickAddButton.addEventListener('click', (event) => {
        this.showQuickAddOptions(event);
      }, true);
    }
  }

  setupHoverListeners() {
    // Find the parent product card element
    this.productCard = this.closest('.product-card');
    
    if (this.productCard) {
      this.productCard.addEventListener('mouseleave', this.handleCardLeave.bind(this));
    }
  }
  
  initOptionSelection() {
    if (!this.optionGroups || this.optionGroups.length === 0) return;
    
    const preSelectedGroup = this.querySelector('[data-quick-add-option][data-pre-selected="true"]');
    if (!preSelectedGroup) return;
    
    const firstInput = preSelectedGroup.querySelector('input[type="radio"]');
    if (!firstInput) return;
    
    const optionIndex = parseInt(firstInput.dataset.optionIndex, 10);
    this.selectedOptions[optionIndex] = firstInput.value;
    firstInput.checked = true;
    
    this.updateAvailableOptions();
  }

  handleCardLeave() {
    // Hide quick add options and show button when leaving card
    if (this.optionsPanel && !this.optionsPanel.hidden) {
      this.hideQuickAddOptions();
    }
  }

  showQuickAddOptions(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (this.optionsPanel) {
      this.optionsPanel.hidden = false;
    }
    
    if (this.quickAddButton) {
      this.quickAddButton.hidden = true;
    }
    
    // Add class to parent product card when options are shown
    const productCard = this.closest('.product-card');
    if (productCard) {
      productCard.classList.add('product-card--add-to-cart-active');
    }
  }

  hideQuickAddOptions() {
    if (this.optionsPanel) {
      this.optionsPanel.hidden = true;
    }
    
    if (this.quickAddButton) {
      this.quickAddButton.hidden = false;
    }
    
    // Remove class from parent product card when options are hidden
    const productCard = this.closest('.product-card');
    if (productCard) {
      productCard.classList.remove('product-card--add-to-cart-active');
    }
    
    // Reset options when hiding
    this.resetOptions();
  }
  
  handleOptionChange(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const input = event.target;
    const optionIndex = parseInt(input.dataset.optionIndex, 10);
    const optionValue = input.value;
    
    this.selectedOptions[optionIndex] = optionValue;
    
    this.userSelectedOptions.add(optionIndex);
    
    this.updateAvailableOptions();
    
    this.checkAndAddToCart();
  }
  
  updateAvailableOptions() {
    if (!this.variants || !this.optionGroups) return;
    
    this.optionGroups.forEach(group => {
      const optionIndex = parseInt(group.dataset.optionIndex, 10);
      
      if (this.userSelectedOptions.has(optionIndex)) return;
      
      const inputs = group.querySelectorAll('input[type="radio"]');
      
      inputs.forEach(input => {
        const optionValue = input.value;
        
        const potentialSelection = [...this.selectedOptions];
        potentialSelection[optionIndex] = optionValue;
        
        const isAvailable = this.isVariantAvailable(potentialSelection);
        
        input.disabled = !isAvailable;
      });
    });
  }
  
  isVariantAvailable(options) {
    const selectedOptions = options.filter(option => option !== null);
    
    if (selectedOptions.length === 0) return true;
    
    return this.variants.some(variant => {
      const matches = options.every((option, index) => {
        return option === null || variant.options[index] === option;
      });
      
      return matches && variant.available;
    });
  }
  
  findMatchingVariant() {
    if (!this.variants) return null;
    
    const allSelected = this.selectedOptions.every(option => option !== null);
    if (!allSelected) return null;
    
    return this.variants.find(variant => {
      return this.selectedOptions.every((option, index) => {
        return variant.options[index] === option;
      });
    });
  }
  
  checkAndAddToCart() {
    let requiredSelections = this.totalOptions;
    
    const selectedCount = this.selectedOptions.filter(option => option !== null).length;
    
    if (selectedCount >= requiredSelections) {
      const matchingVariant = this.findMatchingVariant();
      
      if (matchingVariant && matchingVariant.available) {
        if (this.variantIdInput) {
          this.variantIdInput.value = matchingVariant.id;
        }
        
        this.submitForm();
      }
    }
  }
  
  resetOptions() {
    this.selectedOptions = new Array(this.totalOptions).fill(null);
    
    this.userSelectedOptions = new Set();
    
    this.optionGroups.forEach(group => {
      const inputs = group.querySelectorAll('input[type="radio"]');
      inputs.forEach(input => {
        input.checked = false;
      });
      
      const isPre = group.hasAttribute('data-pre-selected');
      if (isPre) {
        const firstInput = group.querySelector('input[type="radio"]');
        if (firstInput) {
          firstInput.checked = true;
          const optionIndex = parseInt(firstInput.dataset.optionIndex, 10);
          this.selectedOptions[optionIndex] = firstInput.value;
        }
      }
    });
    
    this.updateAvailableOptions();
  }

  submitForm() {
    if (!this.form) return;
    
    this.classList.add('product-card__quick-add-success');
    
    // Remove the active class when form is submitted
    const productCard = this.closest('.product-card');
    
    if (this.submitButton) {
      this.submitButton.click();
    } else {
      this.form.submit();
    }
    
    if (this.optionsPanel) {
      setTimeout(() => {
        this.optionsPanel.hidden = true;
        
        if (this.quickAddButton) {
          this.quickAddButton.hidden = false;
        }
        
        this.resetOptions();
        if (productCard) {
          productCard.classList.remove('product-card--add-to-cart-active');
        }
        
        setTimeout(() => {
          this.classList.remove('product-card__quick-add-success');
        }, 500);
      }, 500);
    } else {
      setTimeout(() => {
        if (productCard) {
          productCard.classList.remove('product-card--add-to-cart-active');
        }
        this.classList.remove('product-card__quick-add-success');
        
        if (this.quickAddButton) {
          this.quickAddButton.hidden = false;
        }
        
        this.resetOptions();
      }, 1000);
    }
  }
}

if (!customElements.get('product-card-quick-add')) {
  customElements.define('product-card-quick-add', ProductCardQuickAdd);
}